// src/clock/useClockInLogic.js
//
// Single source of truth for the tech clock-in panel on Home.
//
// Location services, in one place:
//   - getLocation()        one GPS read with a timeout and readable errors
//   - live tracking        while clocked in, writes liveLocation / route /
//                          lastUpdatedLive on the active time_logs doc
//   - ping response        a supervisor sets pingRequestedAt on the active log;
//                          this hook answers with a fresh GPS read
//   - geofence             per-user: users/{uid}.geofenceEnabled +
//                          users/{uid}.assignedJobSite (job_sites/{id})
//   - offline queue        points captured offline are flushed on reconnect
//
// Fields written to time_logs (all additive, existing fields unchanged):
//   clockInLocation   {latitude, longitude, accuracy, timestamp}
//   clockInGeofence   {siteId, siteName, radiusMeters, distanceMeters,
//                      inside, enforced}
//   liveLocation      {latitude, longitude, accuracy, timestamp}
//   lastUpdatedLive   Date
//   route             [{latitude, longitude, accuracy, timestamp}]
//   geofenceInside    true | false   (only when a site is assigned)
//   pingAnsweredFor   millis of the pingRequestedAt that was answered
//   pingRespondedAt   Date
//   pingError         string | null

import { useState, useEffect, useRef, useCallback } from "react";
import moment from "moment";
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  query,
  where,
  updateDoc,
  doc,
  serverTimestamp,
  arrayUnion,
  deleteDoc,
  onSnapshot,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { calculateHalfMonthStats } from "./calculateHalfMonthStats";
import { closeOpenVisitsForUser } from "./jobVisits";
import { tNow } from "../i18n";
import { getViewAs, blockIfViewingAs, silentlyBlockedByViewAs } from "../utils/viewAs";

// How often a clocked-in device sends its position (ms).
export const LIVE_UPDATE_MS = 60 * 1000;
// Give up on a single GPS read after this long (ms).
const GPS_TIMEOUT_MS = 20 * 1000;
// Phone GPS indoors is often 30–80 m off. When checking a geofence we give the
// worker the benefit of the reported accuracy, capped so a wildly inaccurate
// fix can't open the fence completely.
const MAX_ACCURACY_BUFFER_M = 100;

const getOfflineKey = (uid, logId) => `offlineRoute_${uid}_${logId}`;
const deg2rad = (deg) => (deg * Math.PI) / 180;

export const distanceInMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Shared geofence test. Returns null when there is nothing to test.
export const checkGeofence = (loc, site) => {
  if (!loc || !site) return null;
  const lat = Number(loc.latitude);
  const lng = Number(loc.longitude);
  const sLat = Number(site.latitude);
  const sLng = Number(site.longitude);
  const radius = Number(site.radiusMeters) || 0;
  if ([lat, lng, sLat, sLng].some((n) => Number.isNaN(n))) return null;

  const dist = distanceInMeters(lat, lng, sLat, sLng);
  const buffer = Math.min(Number(loc.accuracy) || 0, MAX_ACCURACY_BUFFER_M);

  return {
    distanceMeters: Math.round(dist),
    radiusMeters: radius,
    bufferMeters: Math.round(buffer),
    inside: dist - buffer <= radius,
  };
};

const GEO_ERROR_TEXT = {
  1: "Location is blocked for this app. Turn on Location for this website in your phone/browser settings, then tap Retry Location.",
  2: "Your location is unavailable. Make sure Location / GPS is turned on for your phone, then tap Retry Location.",
  3: "Getting your location took too long. Move near a window or outside and tap Retry Location.",
};

// One GPS read. Rejects with an Error whose message is safe to show a worker.
export const getLocation = () =>
  new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error(tNow("This device or browser does not support location.")));
      return;
    }
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      reject(new Error(tNow("Location only works on the secure (https) version of the app.")));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy ?? null,
          timestamp: pos.timestamp || Date.now(),
        }),
      (err) => {
        const e = new Error(GEO_ERROR_TEXT[err?.code] ? tNow(GEO_ERROR_TEXT[err.code]) : err?.message || tNow("Unable to get location."));
        e.code = err?.code;
        reject(e);
      },
      { enableHighAccuracy: true, timeout: GPS_TIMEOUT_MS, maximumAge: 15000 }
    );
  });

// users/{uid} is the canonical record (Staff.js writes it that way). Older
// records may only be findable by authUid, so fall back to that.
const findUserDocId = async (uid) => {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    if (snap.exists()) return snap.id;
  } catch (e) {
    console.warn("users/{uid} lookup failed, trying authUid:", e);
  }
  const q = await getDocs(query(collection(db, "users"), where("authUid", "==", uid)));
  return q.docs[0]?.id || null;
};

const loadUserRecord = async (uid) => {
  const id = await findUserDocId(uid);
  if (!id) return null;
  const snap = await getDoc(doc(db, "users", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

const toMillis = (v) => {
  if (!v) return null;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  const n = new Date(v).getTime();
  return Number.isNaN(n) ? null : n;
};

export function useClockInLogic() {
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [currentLogId, setCurrentLogId] = useState(null);
  const [timeLogs, setTimeLogs] = useState([]);
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState("");
  const [userRecord, setUserRecord] = useState(null);
  const [assignedSite, setAssignedSite] = useState(null);
  const [assignedJobId, setAssignedJobId] = useState(null);
  const [liveLocation, setLiveLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [locationError, setLocationError] = useState(null);
  const [locationPermission, setLocationPermission] = useState("unknown");
  const [isLocating, setIsLocating] = useState(false);
  const [lastSentAt, setLastSentAt] = useState(null);

  // Refs so timers and listeners always see current values.
  const userIdRef = useRef(null);
  const userNameRef = useRef("");
  const logIdRef = useRef(null);
  const clockedInRef = useRef(false);
  const siteRef = useRef(null);
  const lastInsideRef = useRef(null);
  const pushingRef = useRef(false);
  const answeringPingRef = useRef(null);

  useEffect(() => { userIdRef.current = userId; }, [userId]);
  useEffect(() => { userNameRef.current = userName; }, [userName]);
  useEffect(() => { logIdRef.current = currentLogId; }, [currentLogId]);
  useEffect(() => { clockedInRef.current = isClockedIn; }, [isClockedIn]);
  useEffect(() => { siteRef.current = assignedSite; }, [assignedSite]);

  // -------------------------------
  // TIME LOGS
  // -------------------------------
  const fetchTimeLogs = async (uid) => {
    const snapshot = await getDocs(
      query(collection(db, "time_logs"), where("userId", "==", uid))
    );
    const logs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    setTimeLogs(logs);

    const active = logs.find((l) => !l.clockOut);
    if (active) {
      setIsClockedIn(true);
      setCurrentLogId(active.id);
      setSelectedLocation(active.clockInLocation || null);
      setLiveLocation((prev) => prev || active.liveLocation || active.clockInLocation || null);
    } else {
      setIsClockedIn(false);
      setCurrentLogId(null);
      // keep last known live location so the map does not get stuck
      setSelectedLocation(null);
    }
  };

  // -------------------------------
  // AUTH + LIVE USER RECORD
  // -------------------------------
  useEffect(() => {
    let unsubUser = null;

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (unsubUser) { unsubUser(); unsubUser = null; }
      if (!user) return;

      // "View as" (Admin only): load the viewed person's clock, read-only.
      const uid = getViewAs()?.uid || user.uid;
      setUserId(uid);

      try {
        const docId = await findUserDocId(uid);
        if (docId) {
          // Live: a supervisor changing geofence settings applies right away.
          unsubUser = onSnapshot(doc(db, "users", docId), (snap) => {
            const data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
            setUserRecord(data);
            if (data) {
              const name = `${data.firstName || ""} ${data.lastName || ""}`.trim();
              setUserName(name || user.displayName || user.email || "");
              setAssignedJobId(data.assignedJobId || null);
            }
          });
        } else {
          setUserName(user.displayName || user.email || "");
        }
      } catch (err) {
        console.warn("User record load failed:", err);
      }

      await fetchTimeLogs(uid);

      // Initial position so the map shows immediately.
      try {
        const gps = await getLocation();
        setLiveLocation(gps);
        setGpsAccuracy(gps.accuracy ?? null);
        setLocationError(null);
      } catch (err) {
        console.warn("Initial GPS load failed:", err);
        setLocationError(err.message);
      }
    });

    return () => {
      unsubAuth();
      if (unsubUser) unsubUser();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Assigned job site follows the user record live.
  const assignedSiteId = userRecord?.assignedJobSite || null;
  useEffect(() => {
    lastInsideRef.current = null;
    if (!assignedSiteId) {
      setAssignedSite(null);
      return;
    }
    const unsub = onSnapshot(
      doc(db, "job_sites", assignedSiteId),
      (snap) => setAssignedSite(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      (err) => console.warn("Job site listen failed:", err)
    );
    return () => unsub();
  }, [assignedSiteId]);

  // -------------------------------
  // PERMISSION STATE (where supported)
  // -------------------------------
  useEffect(() => {
    let status = null;
    const onChange = () => setLocationPermission(status?.state || "unknown");
    if (navigator?.permissions?.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((s) => {
          status = s;
          setLocationPermission(s.state);
          s.addEventListener?.("change", onChange);
        })
        .catch(() => {});
    }
    return () => status?.removeEventListener?.("change", onChange);
  }, []);

  // -------------------------------
  // ONLINE / OFFLINE
  // -------------------------------
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    if (!userId || !currentLogId) {
      setOfflineQueue([]);
      return;
    }
    try {
      const stored = localStorage.getItem(getOfflineKey(userId, currentLogId));
      setOfflineQueue(stored ? JSON.parse(stored) : []);
    } catch {
      setOfflineQueue([]);
    }
  }, [userId, currentLogId]);

  const queueOffline = (point) => {
    const uid = userIdRef.current;
    const logId = logIdRef.current;
    setOfflineQueue((prev) => {
      const updated = [...prev, point];
      try {
        if (uid && logId) localStorage.setItem(getOfflineKey(uid, logId), JSON.stringify(updated));
      } catch {}
      return updated;
    });
  };

  useEffect(() => {
    if (!isOnline || !userId || !currentLogId || offlineQueue.length === 0) return;
    if (silentlyBlockedByViewAs()) return;
    const flush = async () => {
      try {
        await updateDoc(doc(db, "time_logs", currentLogId), {
          route: arrayUnion(...offlineQueue),
          liveLocation: offlineQueue[offlineQueue.length - 1],
          lastUpdatedLive: new Date(),
        });
        try { localStorage.removeItem(getOfflineKey(userId, currentLogId)); } catch {}
        setOfflineQueue([]);
      } catch (err) {
        console.log("Offline route flush failed:", err);
      }
    };
    flush();
  }, [isOnline, offlineQueue, userId, currentLogId]);

  // -------------------------------
  // LIVE LOCATION PUSH
  // -------------------------------
  // Reads GPS and writes it to the active log. `extra` is merged into the same
  // write (used by ping responses).
  const pushLiveLocation = useCallback(async (extra = {}) => {
    // Never send the Admin's GPS into someone else's time log.
    if (silentlyBlockedByViewAs()) return;
    const logId = logIdRef.current;
    const uid = userIdRef.current;
    if (!clockedInRef.current || !logId || !uid) return;
    if (pushingRef.current && !extra.pingAnsweredFor) return;
    pushingRef.current = true;

    try {
      let gps;
      try {
        gps = await getLocation();
      } catch (err) {
        setLocationError(err.message);
        if (extra.pingAnsweredFor) {
          await updateDoc(doc(db, "time_logs", logId), {
            ...extra,
            pingRespondedAt: new Date(),
            pingError: err.message,
          }).catch(() => {});
        }
        return;
      }

      setLiveLocation(gps);
      setGpsAccuracy(gps.accuracy ?? null);
      setLocationError(null);

      const point = {
        latitude: gps.latitude,
        longitude: gps.longitude,
        accuracy: gps.accuracy ?? null,
        timestamp: Date.now(),
      };

      const update = {
        liveLocation: point,
        lastUpdatedLive: new Date(),
        route: arrayUnion(point),
        ...extra,
      };
      if (extra.pingAnsweredFor) {
        update.pingRespondedAt = new Date();
        update.pingError = null;
      }

      // Geofence transition tracking (any assigned site, enforced or not).
      const site = siteRef.current;
      const geo = checkGeofence(point, site);
      if (geo) {
        update.geofenceInside = geo.inside;
        const prev = lastInsideRef.current;
        lastInsideRef.current = geo.inside;
        if (prev !== null && prev !== geo.inside) {
          addDoc(collection(db, "geofence_events"), {
            userId: uid,
            userName: userNameRef.current || "",
            logId,
            siteId: site.id,
            siteName: site.name || "",
            eventType: geo.inside ? "ENTER" : "EXIT",
            distanceMeters: geo.distanceMeters,
            timestamp: serverTimestamp(),
            location: { lat: point.latitude, lng: point.longitude },
          }).catch((e) => console.log("Geofence event log failed:", e));
        }
      }

      if (navigator.onLine) {
        try {
          await updateDoc(doc(db, "time_logs", logId), update);
          setLastSentAt(new Date());
        } catch (err) {
          console.log("Live location write failed, queuing:", err);
          queueOffline(point);
        }
      } else {
        queueOffline(point);
      }
    } finally {
      pushingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodic updates + refresh when the app comes back to the foreground.
  useEffect(() => {
    if (!isClockedIn || !currentLogId) return;

    pushLiveLocation();
    const interval = setInterval(() => pushLiveLocation(), LIVE_UPDATE_MS);

    const onVisible = () => {
      if (document.visibilityState === "visible") pushLiveLocation();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isClockedIn, currentLogId, pushLiveLocation]);

  // Answer supervisor pings on the active log.
  useEffect(() => {
    if (!currentLogId) return;
    const unsub = onSnapshot(
      doc(db, "time_logs", currentLogId),
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        const requested = toMillis(data.pingRequestedAt);
        if (!requested) return;
        if (data.pingAnsweredFor === requested) return;
        if (answeringPingRef.current === requested) return;
        answeringPingRef.current = requested;
        pushLiveLocation({ pingAnsweredFor: requested });
      },
      (err) => console.warn("Ping listener failed:", err)
    );
    return () => unsub();
  }, [currentLogId, pushLiveLocation]);

  // Manual retry from the clock panel.
  const refreshLocation = async () => {
    setIsLocating(true);
    try {
      const gps = await getLocation();
      setLiveLocation(gps);
      setGpsAccuracy(gps.accuracy ?? null);
      setLocationError(null);
      if (clockedInRef.current) pushLiveLocation();
    } catch (err) {
      setLocationError(err.message);
    } finally {
      setIsLocating(false);
    }
  };

  // -------------------------------
  // CLOCK IN / CLOCK OUT
  // -------------------------------
  const removeTodayAutoAssignmentLogs = async (uid) => {
    const today = moment().format("YYYY-MM-DD");
    const snapshot = await getDocs(
      query(collection(db, "time_logs"), where("userId", "==", uid))
    );
    const matches = snapshot.docs.filter((d) => {
      const log = d.data();
      const clockIn = log.clockIn?.toDate ? log.clockIn.toDate() : log.clockIn;
      return (
        log.autoGenerated === true &&
        log.source === "JobEstimateSpreadsheetTechAssign" &&
        clockIn &&
        moment(clockIn).format("YYYY-MM-DD") === today
      );
    });
    await Promise.all(matches.map((d) => deleteDoc(doc(db, "time_logs", d.id))));
  };

  const handleClockToggle = async () => {
    if (blockIfViewingAs()) return;
    if (!userId) return;

    if (!isClockedIn) {
      // CLOCK IN
      setLocationError(null);
      setIsLocating(true);
      try {
        let gps;
        try {
          gps = await getLocation();
        } catch (err) {
          setLocationError(err.message);
          alert(`${tNow("Clock-in needs your location.")}\n\n${err.message}`);
          return;
        }
        setGpsAccuracy(gps.accuracy ?? null);

        // Fresh read of the user's settings at the moment of clock-in.
        const settings = await loadUserRecord(userId);
        const geofenceEnabled = settings?.geofenceEnabled === true;
        const siteId = settings?.assignedJobSite || null;

        let site = null;
        if (siteId) {
          const siteSnap = await getDoc(doc(db, "job_sites", siteId));
          if (siteSnap.exists()) site = { id: siteSnap.id, ...siteSnap.data() };
        }

        const geo = checkGeofence(gps, site);

        if (geofenceEnabled && site && geo && !geo.inside) {
          const msg = tNow("You must be at {site} to clock in (within {radius}m). You are about {dist}m away.", {
            site: site.name,
            radius: geo.radiusMeters,
            dist: geo.distanceMeters,
          });
          setLocationError(msg);
          alert(
            tNow("You are too far from the job site.") +
              `\n${tNow("Required")}: ${site.name}\n${tNow("Allowed")}: ${geo.radiusMeters}m\n${tNow("Distance")}: ${geo.distanceMeters}m` +
              (gps.accuracy ? `\n${tNow("GPS accuracy")}: ±${Math.round(gps.accuracy)}m` : "")
          );
          return;
        }

        // A real clock-in replaces the spreadsheet's automatic 8-hour placeholder.
        await removeTodayAutoAssignmentLogs(userId);

        const now = new Date();
        const point = { ...gps, timestamp: now.getTime() };
        const name =
          userName ||
          `${settings?.firstName || ""} ${settings?.lastName || ""}`.trim();

        const docRef = await addDoc(collection(db, "time_logs"), {
          userId,
          userName: name,
          jobId: assignedJobId || null,
          clockIn: now,
          clockOut: null,
          duration: null,
          clockInLocation: point,
          liveLocation: point,
          lastUpdatedLive: now,
          route: [point],
          clockInGeofence: site
            ? {
                siteId: site.id,
                siteName: site.name || "",
                radiusMeters: geo?.radiusMeters ?? null,
                distanceMeters: geo?.distanceMeters ?? null,
                inside: geo?.inside ?? null,
                enforced: geofenceEnabled,
              }
            : null,
        });

        lastInsideRef.current = geo ? geo.inside : null;
        setIsClockedIn(true);
        setCurrentLogId(docRef.id);
        setSelectedLocation(point);
        setLiveLocation(point);
        setLastSentAt(now);

        try { localStorage.removeItem(getOfflineKey(userId, docRef.id)); } catch {}
        setOfflineQueue([]);

        fetchTimeLogs(userId);
      } catch (err) {
        console.error(err);
        setLocationError(tNow("Clock-in failed. Please try again."));
      } finally {
        setIsLocating(false);
      }
    } else {
      // CLOCK OUT
      setIsLocating(true);
      try {
        // Clock-out should never be blocked by GPS trouble.
        let gps = null;
        try {
          gps = await getLocation();
          setGpsAccuracy(gps.accuracy ?? null);
        } catch (err) {
          console.warn("Clock-out without GPS:", err);
          setLocationError(err.message);
        }

        const now = new Date();
        const logRef = doc(db, "time_logs", currentLogId);

        let log = timeLogs.find((l) => l.id === currentLogId);
        if (!log) {
          const snap = await getDoc(logRef);
          if (!snap.exists()) {
            alert(tNow("Clock-out failed: active time log not found. Please refresh and try again."));
            setLocationError(tNow("Active time log not found."));
            return;
          }
          log = { id: snap.id, ...snap.data() };
        }

        const clockInDate = log.clockIn?.toDate
          ? log.clockIn.toDate()
          : log.clockIn instanceof Date
          ? log.clockIn
          : null;

        if (!clockInDate) {
          alert(tNow("Clock-out failed: clock-in time was missing. Please contact admin."));
          setLocationError(tNow("Clock-in time missing on active log."));
          return;
        }

        const duration = Math.round((now - clockInDate) / 60000);

        await updateDoc(logRef, {
          clockOut: now,
          clockOutLocation: gps,
          duration,
        });

        // Job check-ins live inside the work day. Clocking out for the day
        // checks them out of any job they forgot to leave, so no job timer
        // keeps running overnight. Never blocks the clock-out itself.
        try {
          await closeOpenVisitsForUser(userId, "clock_out", gps);
        } catch (visitErr) {
          console.warn("Auto check-out of open job on clock-out failed:", visitErr);
        }

        setIsClockedIn(false);
        setCurrentLogId(null);
        lastInsideRef.current = null;

        try { localStorage.removeItem(getOfflineKey(userId, logRef.id)); } catch {}
        setOfflineQueue([]);

        fetchTimeLogs(userId);
      } catch (err) {
        console.error(err);
        setLocationError(tNow("Clock-out failed. Please try again."));
      } finally {
        setIsLocating(false);
      }
    }
  };

  // -------------------------------
  // LUNCH CLOCK-IN / CLOCK-OUT (behavior unchanged)
  // -------------------------------
  const handleLunchToggle = async () => {
    if (blockIfViewingAs()) return;
    if (!userId) return;

    if (!isClockedIn) {
      try {
        const gps = await getLocation();
        setGpsAccuracy(gps.accuracy ?? null);
        const now = new Date();
        const point = { ...gps, timestamp: now.getTime() };

        const docRef = await addDoc(collection(db, "time_logs"), {
          userId,
          userName,
          isLunch: true,
          clockIn: now,
          clockOut: null,
          duration: null,
          clockInLocation: point,
          liveLocation: point,
          lastUpdatedLive: now,
        });

        setIsClockedIn(true);
        setCurrentLogId(docRef.id);
        setSelectedLocation(point);
        setLiveLocation(point);

        try { localStorage.removeItem(getOfflineKey(userId, docRef.id)); } catch {}
        setOfflineQueue([]);

        fetchTimeLogs(userId);
      } catch (err) {
        console.error(err);
        setLocationError(err?.message || tNow("Unable to fetch location."));
      }
    } else {
      try {
        let gps = null;
        try {
          gps = await getLocation();
          setGpsAccuracy(gps.accuracy ?? null);
        } catch (err) {
          setLocationError(err.message);
        }

        const now = new Date();
        const logRef = doc(db, "time_logs", currentLogId);

        let log = timeLogs.find((l) => l.id === currentLogId);
        if (!log) {
          const snap = await getDoc(logRef);
          if (!snap.exists()) {
            alert(tNow("Lunch clock-out failed: active log not found. Please refresh and try again."));
            setLocationError(tNow("Active lunch log not found."));
            return;
          }
          log = { id: snap.id, ...snap.data() };
        }

        const clockInDate = log.clockIn?.toDate
          ? log.clockIn.toDate()
          : log.clockIn instanceof Date
          ? log.clockIn
          : null;

        if (!clockInDate) {
          alert(tNow("Lunch clock-out failed: clock-in time missing."));
          setLocationError(tNow("Clock-in time missing on lunch log."));
          return;
        }

        const duration = Math.round((now - clockInDate) / 60000);

        await updateDoc(logRef, {
          clockOut: now,
          duration,
          clockOutLocation: gps,
        });

        setIsClockedIn(false);
        setCurrentLogId(null);

        try { localStorage.removeItem(getOfflineKey(userId, logRef.id)); } catch {}
        setOfflineQueue([]);

        fetchTimeLogs(userId);
      } catch (err) {
        console.error(err);
        setLocationError(tNow("Clock-out failed. Please try again."));
      }
    }
  };

  // -------------------------------
  // UI HELPERS
  // -------------------------------
  const mapLocation = liveLocation || selectedLocation;

  const isInsideGeofence = () => {
    const geo = checkGeofence(mapLocation, assignedSite);
    return geo ? geo.inside : null;
  };

  const getGeofenceStatus = () => checkGeofence(mapLocation, assignedSite);

  const getETA = () => {
    const geo = checkGeofence(mapLocation, assignedSite);
    if (!geo) return null;
    return { dist: geo.distanceMeters, etaMinutes: Math.ceil(geo.distanceMeters / 1.4 / 60) };
  };

  const getTodaysHours = () => {
    const today = moment().format("YYYY-MM-DD");
    const todaysLogs = timeLogs.filter((log) => {
      if (!log.clockIn || !log.duration) return false;
      const logDay = moment(
        log.clockIn instanceof Date ? log.clockIn : log.clockIn.toDate()
      ).format("YYYY-MM-DD");
      return logDay === today;
    });
    const totalMinutes = todaysLogs.reduce((sum, log) => sum + log.duration, 0);
    return (totalMinutes / 60).toFixed(2);
  };

  const stats = calculateHalfMonthStats(timeLogs);

  return {
    userId,
    userName,
    currentLogId,
    isClockedIn,
    handleClockToggle,
    handleLunchToggle,
    timeLogs,
    mapLocation,
    assignedSite,
    geofenceEnabled: userRecord?.geofenceEnabled === true,
    gpsAccuracy,
    isOnline,
    getETA,
    isInsideGeofence,
    getGeofenceStatus,
    getTodaysHours,
    stats,
    locationError,
    locationPermission,
    isLocating,
    lastSentAt,
    pendingOfflinePoints: offlineQueue.length,
    refreshLocation,
  };
}
